import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "Qoo10 商品登録「画像が要件を満たしていません」正しいサイズと形式",
	description:
		"Qoo10で商品登録時に「画像が要件を満たしていません」エラーが出る原因と、正しい画像サイズ・形式の設定方法をわかりやすく解説。",
	publishDate: "2026-03-16",
	slug: "qoo10-product-image-spec",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function Qoo10ProductImageSpec() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<p className="text-gray-700 mb-6">
						Qoo10で商品を登録しようとしたら「
						<strong>画像が要件を満たしていません</strong>
						」というエラーが出て、商品が公開できない——そんな経験、ありませんか？
						このエラーは原因さえわかれば必ず解決できます。本記事では、Qoo10の画像要件と対処法を詳しく解説します。
					</p>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							Qoo10の商品画像 基本要件
						</h2>
						<p className="text-gray-700 mb-4">
							Qoo10に登録できる商品画像には以下の要件があります。
						</p>
						<div className="overflow-x-auto mb-4">
							<table className="w-full text-sm border-collapse border border-gray-200">
								<thead>
									<tr className="bg-gray-50">
										<th className="border border-gray-200 px-4 py-2 text-left">
											項目
										</th>
										<th className="border border-gray-200 px-4 py-2 text-left">
											要件
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											最小サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											400×400px 以上
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											推奨サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											800×800px（正方形推奨）
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											ファイル形式
										</td>
										<td className="border border-gray-200 px-4 py-2">
											JPEG（.jpg）、PNG（.png）
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											最大ファイルサイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											5MB 以内
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											最大登録枚数
										</td>
										<td className="border border-gray-200 px-4 py-2">15枚</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							「画像が要件を満たしていません」エラーの主な原因
						</h2>
						<p className="text-gray-700 mb-4">
							このエラーが発生する主な原因は以下のとおりです。
						</p>
						<ol className="list-decimal list-inside space-y-3 text-gray-700">
							<li>
								<strong>画像サイズが小さすぎる</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									400×400px未満の画像はアップロード自体が拒否されます。スマートフォンで撮影した縮小画像や、Webから引用した低解像度画像で発生しがちです。
								</span>
							</li>
							<li>
								<strong>非対応のファイル形式</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									WebP・GIF・BMP・TIFFなどはQoo10では使用できません。必ずJPEG（.jpg）またはPNG（.png）に変換してください。
								</span>
							</li>
							<li>
								<strong>ファイルサイズが5MBを超えている</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									高解像度のRAWデータやスマートフォンの高画質写真はそのままだと5MBを超えることがあります。
								</span>
							</li>
							<li>
								<strong>メイン画像への過度な文字入れ</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									1枚目のメイン画像に商品と無関係なテキストや過度な装飾を入れると審査で弾かれることがあります。
								</span>
							</li>
						</ol>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							エラーを解決する手順
						</h2>
						<ol className="list-decimal list-inside space-y-4 text-gray-700">
							<li>
								<strong>画像サイズを確認する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									Windowsなら右クリック→「プロパティ」→「詳細」タブで確認。Macなら「情報を見る（⌘I）」で確認できます。400×400px未満なら元の高解像度データから作り直しましょう。
								</span>
							</li>
							<li>
								<strong>JPEG/PNG形式に変換する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									WebPファイルをJPEGに変換するには、ブラウザで画像を右クリック→「名前を付けて保存」でJPEGを選ぶか、変換ツールを使います。
								</span>
							</li>
							<li>
								<strong>ファイルサイズを5MB以内に圧縮する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									画像圧縮ツールを使って品質を落とさずファイルサイズを削減できます。推奨は800×800pxにリサイズした上でJPEG品質85%程度に設定することです。
								</span>
							</li>
							<li>
								<strong>背景を整える（任意だが効果的）</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									Qoo10のメイン画像に背景色の強制はありませんが、白背景の商品画像は購入率が上がる傾向にあります。
								</span>
							</li>
						</ol>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							手動での変換・リサイズ作業は商品数が多くなると非常に大変です。
							<strong>しろぬきや</strong>
							を使えば、AI背景除去＋Qoo10推奨サイズへの自動リサイズがブラウザ上で完結します。
							画像をサーバーに送信しないためプライバシーも安心。無料で1日5枚まで利用できます。
						</p>
						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
